import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Col, Select, Switch, Typography } from 'antd';

import CustomMaskedInput from 'components/MaskedInput';
import { usePhysicalDeliveryStore } from 'v2/store/PhysicalDelivery';
import { run } from 'validations/ruleRunner';

import Validations from './validations';

const CAIXA_PACOTE = '1',
  ROLO_PRISMA = '2';
const PAC = '41106',
  SEDEX = '40010',
  SEDEX10 = '40215';

interface IProps {
  enabledValidation: boolean;
  onEdit?: () => void;
}

const Correios: React.FC<IProps> = ({ enabledValidation, onEdit }) => {
  const { delivery, setDelivery } = usePhysicalDeliveryStore();
  const [validationErrors, setValidationErrors] = useState<any>([]);
  const prevDeliveryRef = useRef(delivery);
  const prevEnabledValidationRef = useRef(enabledValidation);

  const errorFor = (field: any) => validationErrors[field] || '';
  const isValid = (field: any) => !!validationErrors[field];

  const executeValidation = useCallback(() => {
    setValidationErrors(run(delivery, Validations));
  }, [delivery]);
  const handleChange = useCallback(
    (prop: string, value: any) => {
      if (onEdit) onEdit();
      setDelivery({ ...delivery, [prop]: value });
    },
    [delivery, onEdit, setDelivery]
  );
  const handleChangeType = useCallback(
    (checked: any, value: any) => {
      let options = delivery['delivery_options'] || [];
      options = checked ? [...options, value] : options.filter((item: any) => item !== value);
      if (onEdit) onEdit();
      setDelivery({ ...delivery, delivery_options: options });
    },
    [delivery, onEdit, setDelivery]
  );

  useEffect(() => {
    if (enabledValidation) executeValidation(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  useEffect(() => {
    if (
      (!prevEnabledValidationRef.current && enabledValidation) ||
      (enabledValidation && prevDeliveryRef.current !== delivery)
    )
      executeValidation();
    prevDeliveryRef.current = delivery;
    prevEnabledValidationRef.current = enabledValidation;
  }, [delivery, enabledValidation, executeValidation]);

  const pac = delivery?.delivery_options?.includes(PAC);
  const sedex = delivery?.delivery_options?.includes(SEDEX);
  const sedex10 = delivery?.delivery_options?.includes(SEDEX10);

  return (
    <>
      <Col xs={24} md={8}>
        <Typography.Text className='flex items-center gap-2'>
          <Switch id='product-delivery-pac' checked={pac} onChange={checked => handleChangeType(checked, PAC)} /> PAC
        </Typography.Text>
      </Col>
      <Col xs={24} md={8}>
        <Typography.Text className='flex items-center gap-2'>
          <Switch id='product-delivery-sedex' checked={sedex} onChange={checked => handleChangeType(checked, SEDEX)} />{' '}
          SEDEX
        </Typography.Text>
      </Col>
      <Col xs={24} md={8}>
        <Typography.Text className='flex items-center gap-2'>
          <Switch
            id='product-delivery-sedex-10'
            checked={sedex10}
            onChange={checked => handleChangeType(checked, SEDEX10)}
          />{' '}
          SEDEX 10
        </Typography.Text>
      </Col>
      <Col span={24}>
        {isValid('delivery_options') && (
          <Typography.Text className='text-(--uizz-antd-color-error)'>{errorFor('delivery_options')}</Typography.Text>
        )}
      </Col>

      <Col span={24}>
        <Typography.Text className='mb-1 block text-base'>Formato de embalagem</Typography.Text>
        <Select
          id='format'
          className='w-full'
          value={delivery?.format ? String(delivery.format) : undefined}
          onChange={(value: any) => handleChange('format', value)}
          options={[
            { label: 'Caixa/Pacote', value: CAIXA_PACOTE },
            { label: 'Rolo/Prisma', value: ROLO_PRISMA }
          ]}
        />
      </Col>
      <Col span={24}>
        <CustomMaskedInput
          label='CEP de origem'
          fullWidth={true}
          disableUnderline
          id='zip_code'
          mask='postalCode'
          helperText={isValid('zip_code') ? errorFor('zip_code') : null}
          error={isValid('zip_code')}
          value={delivery.zip_code}
          onChange={(value: any) => handleChange('zip_code', value)}
          InputLabelProps={{ shrink: true }}
        />
      </Col>
      <Col span={24}>
        <CustomMaskedInput
          label='Peso'
          fullWidth={true}
          disableUnderline
          id='weight'
          mask='money'
          helperText={isValid('weight') ? errorFor('weight') : null}
          error={isValid('weight')}
          value={delivery.weight}
          onChange={(value: any) => handleChange('weight', value)}
          InputLabelProps={{ shrink: true }}
          endAdornment='kg'
        />
      </Col>
      {delivery.format == CAIXA_PACOTE && (
        <Col span={24}>
          <CustomMaskedInput
            label='Altura'
            fullWidth={true}
            disableUnderline
            id='height'
            mask='number'
            helperText={
              isValid('height') ? errorFor('height') : 'A altura não pode ser menor que 1cm ou maior que 100cm'
            }
            error={isValid('height')}
            value={delivery.height}
            onChange={(value: any) => handleChange('height', value)}
            InputLabelProps={{ shrink: true }}
            endAdornment='cm'
          />
        </Col>
      )}
      {delivery.format != ROLO_PRISMA && (
        <Col span={24}>
          <CustomMaskedInput
            label='Largura'
            fullWidth={true}
            disableUnderline
            id='width'
            mask='number'
            helperText={
              isValid('width') ? errorFor('width') : 'A largura não pode ser menor que 8cm ou maior que 100cm'
            }
            error={isValid('width')}
            value={delivery.width}
            onChange={(value: any) => handleChange('width', value)}
            InputLabelProps={{ shrink: true }}
            endAdornment='cm'
          />
        </Col>
      )}
      <Col span={24}>
        <CustomMaskedInput
          label='Comprimento'
          fullWidth={true}
          disableUnderline
          id='length'
          mask='number'
          helperText={
            isValid('length')
              ? errorFor('length')
              : delivery?.format == CAIXA_PACOTE || !delivery
                ? 'O comprimento não pode ser menor que 13cm ou maior que 100cm'
                : 'O comprimento não pode ser menor que 18cm ou maior que 100cm'
          }
          error={isValid('length')}
          value={delivery.length}
          onChange={(value: any) => handleChange('length', value)}
          InputLabelProps={{ shrink: true }}
          endAdornment='cm'
        />
      </Col>
      {delivery.format == ROLO_PRISMA && (
        <Col span={24}>
          <CustomMaskedInput
            label='Diâmetro'
            fullWidth={true}
            disableUnderline
            id='diameter'
            mask='number'
            helperText={
              isValid('diameter') ? errorFor('diameter') : 'O diâmetro não pode ser menor que 5cm ou maior que 91cm'
            }
            error={isValid('diameter')}
            value={delivery.diameter}
            onChange={(value: any) => handleChange('diameter', value)}
            InputLabelProps={{ shrink: true }}
            endAdornment='cm'
          />
        </Col>
      )}
    </>
  );
};

export default Correios;
